import * as React from 'react'
import { CreditCard, Plus, Unplug } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { deleteJSON, getJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { displayDateTime } from '@/lib/dates'
import { useViewOnly, useViewOnlyBadge } from '@/components/templates/app-shell'

interface PaymentSettingsResponse {
  payment_settings: PaymentSettings
}

interface PaymentSettings {
  association_currency: string
  online_payments_available: boolean
  stripe_account: StripeAccount | null
  financial_history: PaymentHistoryEntry[]
  connection_notice?: string | null
  connection_warning?: string | null
  autopay_warning?: string | null
}

interface StripeAccount {
  id: number
  provider: string
  external_account_id: string
  account_name: string
  currency: string
  mode: string
  connected_by: string
  connected_at: string
}

interface PaymentHistoryEntry {
  id: number
  event: string
  actor: string | null
  external_account_id?: string | null
  account_name?: string | null
  currency?: string | null
  mode?: string | null
  occurred_at: string
}

interface StripeAccountForm {
  externalAccountId: string
  accountName: string
  currency: string
}

const initialForm: StripeAccountForm = {
  externalAccountId: '',
  accountName: '',
  currency: 'USD',
}

export function PaymentSettingsPage() {
  const viewOnly = useViewOnly()
  const showViewOnlyBadge = useViewOnlyBadge()
  const queryClient = useQueryClient()
  const [form, setForm] = React.useState<StripeAccountForm>(initialForm)
  const settingsQuery = useQuery({
    queryKey: ['payment-settings'],
    queryFn: () => getJSON<PaymentSettingsResponse>('/api/v1/payment_settings'),
    retry: false,
  })
  const connectAccount = useMutation({
    mutationFn: () => connectStripeAccount(form),
    onSuccess: async (data) => {
      queryClient.setQueryData(['payment-settings'], data)
      await queryClient.invalidateQueries({ queryKey: ['payment-settings'] })
    },
  })
  const createTestAccount = useMutation({
    mutationFn: () => createTestStripeAccount(form),
    onSuccess: async (data) => {
      queryClient.setQueryData(['payment-settings'], data)
      await queryClient.invalidateQueries({ queryKey: ['payment-settings'] })
    },
  })
  const disconnectAccount = useMutation({
    mutationFn: () => deleteJSON('/api/v1/stripe_account_connection'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['payment-settings'] })
    },
  })
  const settings = settingsQuery.data?.payment_settings
  const pending =
    connectAccount.isPending || createTestAccount.isPending || disconnectAccount.isPending
  const error = connectAccount.error ?? createTestAccount.error ?? disconnectAccount.error
  const submitConnect = () => {
    createTestAccount.reset()
    connectAccount.mutate()
  }
  const submitCreateTest = () => {
    connectAccount.reset()
    createTestAccount.mutate()
  }

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header className="pb-4">
        <PaymentSettingsHeader showViewOnlyBadge={showViewOnlyBadge} />
      </header>

      {settingsQuery.isLoading ? (
        <p className="text-body-sm text-muted-foreground" role="status">
          Loading payment settings...
        </p>
      ) : settingsQuery.isError || !settings ? (
        <Alert>Payment settings unavailable</Alert>
      ) : (
        <Card>
          <CardContent className="grid p-0 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
            <div className="divide-y divide-border">
              {settings.connection_notice && <Alert>{settings.connection_notice}</Alert>}
              {settings.connection_warning && <Alert>{settings.connection_warning}</Alert>}
              {settings.autopay_warning && <Alert>{settings.autopay_warning}</Alert>}
              <ConnectionStatus
                settings={settings}
                pending={pending}
                viewOnly={viewOnly}
                onDisconnect={() => {
                  if (
                    window.confirm(
                      'Disconnect Stripe? Online payments will stop and payable surfaces will use offline instructions.',
                    )
                  ) {
                    disconnectAccount.mutate()
                  }
                }}
              />
              {!viewOnly && !settings.stripe_account && (
                <ConnectionForm
                  form={form}
                  error={error}
                  pending={pending}
                  onChange={setForm}
                  onConnect={submitConnect}
                  onCreateTest={submitCreateTest}
                />
              )}
            </div>
            <HistoryPanel history={settings.financial_history} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function PaymentSettingsHeader({ showViewOnlyBadge }: { showViewOnlyBadge: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <h1 className="text-heading-sm font-semibold text-foreground">Payment settings</h1>
      {showViewOnlyBadge && <Badge variant="info">View only</Badge>}
    </div>
  )
}

function ConnectionStatus({
  settings,
  pending,
  viewOnly,
  onDisconnect,
}: {
  settings: PaymentSettings
  pending: boolean
  viewOnly: boolean
  onDisconnect: () => void
}) {
  const account = settings.stripe_account

  return (
    <section className="p-4">
      <h2 className="flex items-center gap-2 text-body font-semibold">
        <CreditCard className="h-4 w-4 text-primary" strokeWidth={1.5} />
        Stripe account
      </h2>
      <div className="pt-3">
        {account ? (
          <div className="space-y-3 text-body-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="success">Connected</Badge>
              <span className="font-medium text-foreground">{account.account_name}</span>
              <span className="text-muted-foreground">{account.external_account_id}</span>
            </div>
            <dl className="grid gap-2 sm:grid-cols-3">
              <Detail label="Currency" value={account.currency} />
              <Detail label="Mode" value={account.mode} />
              <Detail label="Connected" value={displayDateTime(account.connected_at)} />
            </dl>
            {!viewOnly && (
              <div className="flex justify-end">
                <Button variant="quietDestructive" disabled={pending} onClick={onDisconnect}>
                  <Unplug className="mr-2 h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
                  {pending ? 'Disconnecting...' : 'Disconnect Stripe'}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1 text-body-sm">
            <p className="font-medium text-foreground">No Stripe account connected</p>
            <p className="text-caption text-muted-foreground">
              Association currency: {settings.association_currency}
            </p>
          </div>
        )}
      </div>
    </section>
  )
}

function ConnectionForm({
  form,
  error,
  pending,
  onChange,
  onConnect,
  onCreateTest,
}: {
  form: StripeAccountForm
  error: Error | null
  pending: boolean
  onChange: React.Dispatch<React.SetStateAction<StripeAccountForm>>
  onConnect: () => void
  onCreateTest: () => void
}) {
  return (
    <section className="max-w-2xl p-4">
      <h2 className="text-body font-semibold">Connect Stripe</h2>
      <div className="pt-3">
        {stripeAccountFormMessage(error) && (
          <Alert className="mb-4">{stripeAccountFormMessage(error)}</Alert>
        )}
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            onConnect()
          }}
        >
          <FormField
            label="Stripe account ID"
            htmlFor="stripe-account-id"
            error={apiFieldMessages(error, 'external_account_id')[0]}
          >
            <Input
              id="stripe-account-id"
              value={form.externalAccountId}
              placeholder="acct_..."
              onChange={(event) =>
                onChange((current) => ({ ...current, externalAccountId: event.target.value }))
              }
            />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_120px]">
            <FormField
              label="Account name"
              htmlFor="stripe-account-name"
              error={apiFieldMessages(error, 'account_name')[0]}
            >
              <Input
                id="stripe-account-name"
                value={form.accountName}
                onChange={(event) =>
                  onChange((current) => ({ ...current, accountName: event.target.value }))
                }
              />
            </FormField>
            <FormField
              label="Currency"
              htmlFor="stripe-account-currency"
              error={apiFieldMessages(error, 'currency')[0]}
            >
              <Input
                id="stripe-account-currency"
                value={form.currency}
                maxLength={3}
                onChange={(event) =>
                  onChange((current) => ({ ...current, currency: event.target.value }))
                }
              />
            </FormField>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" disabled={pending} onClick={onCreateTest}>
              <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
              Create test account
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Connecting...' : 'Connect account'}
            </Button>
          </div>
        </form>
      </div>
    </section>
  )
}

function HistoryPanel({ history }: { history: PaymentHistoryEntry[] }) {
  return (
    <section className="border-t border-border p-4 lg:border-l lg:border-t-0">
      <h2 className="text-body font-semibold">Financial history</h2>
      <div className="pt-3">
        {history.length === 0 ? (
          <p className="text-body-sm text-muted-foreground">No processor events yet.</p>
        ) : (
          <div className="space-y-3">
            {history.map((entry) => (
              <div key={entry.id} className="text-body-sm">
                <p className="font-medium text-foreground">{entry.event}</p>
                <p className="text-caption text-muted-foreground">
                  {entry.external_account_id}
                  {entry.currency ? ` · ${entry.currency}` : ''}
                  {entry.mode ? ` · ${entry.mode}` : ''}
                </p>
                <p className="text-caption text-muted-foreground">
                  {entry.actor ?? '-'} · {displayDateTime(entry.occurred_at)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-caption text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{value}</dd>
    </div>
  )
}

function connectStripeAccount(form: StripeAccountForm) {
  return postJSON<PaymentSettingsResponse>('/api/v1/stripe_account_connection', {
    stripe_account: {
      external_account_id: form.externalAccountId,
      account_name: form.accountName,
      currency: form.currency,
    },
  })
}

function createTestStripeAccount(form: StripeAccountForm) {
  return postJSON<PaymentSettingsResponse>('/api/v1/stripe_account_connection/test_account', {
    stripe_account: {
      account_name: form.accountName,
      currency: form.currency,
    },
  })
}

function stripeAccountFormMessage(error: Error | null) {
  if (!error) return null

  const providerMessage = apiFieldMessages(error, 'provider')[0]
  if (providerMessage) return providerMessage

  const fieldMessage =
    apiFieldMessages(error, 'external_account_id')[0] ||
    apiFieldMessages(error, 'account_name')[0] ||
    apiFieldMessages(error, 'currency')[0]
  if (fieldMessage) return null

  return error.message
}
